import type { CartLineVm, CartSummaryVm } from '../cart/cart.service';
import type { CatalogProduct } from '../products/product-identity';
import type { ProductDetailVm } from '../products/product-catalog.service';
import type { BenefitPassDetailVm } from '../benefit-pass/benefit-pass.service';
import type { WishlistProductVm } from '../wishlist/wishlist.service';
import type {
  AcquisitionSource,
  AnalyticsAttribution,
  AnalyticsItem,
  AnalyticsItemInput,
  AnalyticsPageView,
  BuyerStatus,
  DeviceType,
  DocumentType,
  LoginErrorType,
  PasswordPopupTriggerContext,
  UserSegment,
} from './data-layer.types';

export const SEGMENT_BY_PREFIX: Readonly<Record<string, UserSegment | undefined>> = {
  '20': 'SEGMENTO_GENERAL',
  '30': 'INFINITE_CLIENTES_NUEVOS',
  '31': 'PREMIUM_CLIENTES_NUEVOS',
  '32': 'PREMIUM_CLIENTES_ACTUALES',
  '33': 'INFINITE_CLIENTES_ACTUALES',
};

export const ANALYTICS_PAGE_TITLES: Readonly<Record<string, string | undefined>> = {
  '/ingreso/ingresar': 'Ingreso - Documento',
  '/ingreso/verificar-membresia': 'Ingreso - Verificar membresia',
  '/checkout/resumen': 'Checkout - Resumen',
  '/checkout/direccion': 'Checkout - Direccion',
  '/checkout/entrega': 'Checkout - Entrega',
  '/checkout/pago': 'Checkout - Pago',
};

const BENEFIT_PASS_CATEGORY = 'Luxury Pass';
const PRODUCT_CATEGORY = 'Productos';

export function money(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function roundMoney(value: unknown): number {
  return Number(money(value).toFixed(2));
}

export function toAnalyticsItem(
  input: AnalyticsItemInput,
  extras: { readonly listId?: string; readonly listName?: string } = {},
): AnalyticsItem | null {
  const itemId = firstText(input.sku, input.id);
  const itemName = firstText(input.name, input.title);
  const price = roundMoney(firstPositive(input.memberPrice, input.luxuryPrice, input.price));
  if (!itemId || !itemName || price <= 0) return null;

  const item: AnalyticsItem = {
    item_id: itemId,
    item_name: itemName,
    item_brand: firstText(input.brand, input.brandName),
    item_category: firstText(input.category),
    price,
    ...(input.quantity && input.quantity > 0 ? { quantity: input.quantity } : {}),
    ...(input.index !== undefined && input.index !== null ? { index: input.index } : {}),
    ...(money(input.regularPrice) > 0 ? { price_regular: roundMoney(input.regularPrice) } : {}),
    ...(extras.listId ? { item_list_id: extras.listId } : {}),
    ...(extras.listName ? { item_list_name: extras.listName } : {}),
  };
  return item;
}

export function productToAnalyticsItem(
  product: CatalogProduct,
  extras: { readonly index?: number; readonly listId?: string; readonly listName?: string } = {},
): AnalyticsItem | null {
  return toAnalyticsItem(
    {
      id: product.id,
      sku: product.sku,
      name: product.title,
      brand: product.brand?.name,
      category: product.category,
      memberPrice: product.memberPrice,
      luxuryPrice: product.luxuryClubPrice,
      price: product.discountPrice ?? product.price,
      regularPrice: product.regularPrice,
      index: extras.index,
    },
    extras,
  );
}

export function productDetailToAnalyticsItem(product: ProductDetailVm): AnalyticsItem | null {
  return toAnalyticsItem({
    id: product.id,
    sku: product.sku,
    name: product.title,
    brand: product.brandName,
    category:
      product.category ?? product.breadcrumbs.at(-2)?.label ?? product.breadcrumbs.at(0)?.label,
    memberPrice: product.specialPrice,
    luxuryPrice: product.plusPrice,
    price: product.pricing?.specialPrice ?? product.specialPrice,
    regularPrice: product.pricing?.regularPrice ?? product.regularPrice,
  });
}

export function benefitPassDetailToAnalyticsItem(
  detail: BenefitPassDetailVm,
): AnalyticsItem | null {
  return toAnalyticsItem({
    id: detail.id,
    sku: detail.sku,
    name: detail.title,
    brand: detail.brandName,
    category: detail.category ?? BENEFIT_PASS_CATEGORY,
    memberPrice: detail.specialPrice,
    luxuryPrice: detail.tierPrice,
    price: detail.specialPrice || detail.tierPrice,
    regularPrice: detail.regularPrice,
  });
}

export function cartLineToAnalyticsItem(line: CartLineVm, index?: number): AnalyticsItem | null {
  return toAnalyticsItem({
    id: line.productId || line.lineId,
    sku: line.sku,
    name: line.title,
    brand: line.brandName,
    category: line.category ?? defaultCategory(line.lineType),
    price: line.unitPrice,
    regularPrice: line.regularPrice || line.plusPrice,
    quantity: line.quantity,
    index,
  });
}

export function cartToAnalyticsItems(cart: CartSummaryVm): AnalyticsItem[] {
  return cart.lines
    .map((line, index) => cartLineToAnalyticsItem(line, index))
    .filter((item): item is AnalyticsItem => item !== null);
}

export function wishlistItemToAnalyticsItem(item: WishlistProductVm): AnalyticsItem | null {
  return toAnalyticsItem({
    id: item.id,
    sku: item.sku,
    name: item.title,
    brand: item.brandName,
    category: item.category ?? defaultCategory(item.itemType),
    memberPrice: item.offerPrice,
    luxuryPrice: item.tierPrice,
    regularPrice: item.regularPrice,
  });
}

export function cartSignature(cart: CartSummaryVm): string {
  return cart.lines
    .map((line) => `${line.lineId}:${line.quantity}`)
    .sort()
    .join('|');
}

export function mapProducts(
  products: readonly CatalogProduct[],
  extras: { readonly listId?: string; readonly listName?: string } = {},
): AnalyticsItem[] {
  return products
    .map((product, index) => productToAnalyticsItem(product, { ...extras, index }))
    .filter((item): item is AnalyticsItem => item !== null);
}

export function normalizeSegment(
  prefix?: string | null,
  memberCode?: string | null,
): UserSegment | '' {
  const fromPrefix = SEGMENT_BY_PREFIX[segmentPrefix(prefix)];
  if (fromPrefix) return fromPrefix;
  return SEGMENT_BY_PREFIX[segmentPrefix(memberCode)] ?? '';
}

export function normalizeBuyerStatus(value?: string | null): BuyerStatus | '' {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'new' || normalized === 'returning' || normalized === 'lapsed') {
    return normalized;
  }
  return '';
}

export function pageViewOf(url: string, title?: string): AnalyticsPageView {
  const path = routePath(url);
  return {
    page_path: path,
    page_location: pageLocation(path),
    page_title: ANALYTICS_PAGE_TITLES[path] ?? title ?? currentTitle(),
  };
}

export function normalizeDocumentType(value: string): DocumentType {
  return value.toUpperCase() === 'CE' ? 'CE' : 'DNI';
}

export function deviceType(): DeviceType {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop';
  if (window.matchMedia('(max-width: 560px)').matches) return 'mobile';
  if (window.matchMedia('(max-width: 768px)').matches) return 'tablet';
  return 'desktop';
}

export function secondsSince(fromMs: number, now = Date.now()): number {
  return Math.max(0, Math.round((now - fromMs) / 1000));
}

export function captureAttribution(
  href = currentHref(),
  referrer = currentReferrer(),
): AnalyticsAttribution {
  const url = safeUrl(href);
  const params = url?.searchParams;
  const source = params?.get('utm_source')?.trim() ?? '';
  const medium = params?.get('utm_medium')?.trim() ?? '';
  const campaign = params?.get('utm_campaign')?.trim() ?? '';
  return {
    source,
    medium,
    campaign,
    acquisitionSource: resolveAcquisitionSource(source, medium, referrer),
  };
}

export function resolveAcquisitionSource(
  source: string,
  medium: string,
  referrer: string,
): AcquisitionSource {
  const haystack = `${source} ${medium}`.toLowerCase();
  if (haystack.includes('email') || haystack.includes('newsletter') || haystack.includes('mail')) {
    return 'email';
  }
  if (
    haystack.includes('meta') ||
    haystack.includes('facebook') ||
    haystack.includes('instagram') ||
    haystack.includes('ig') ||
    haystack.includes('fb')
  ) {
    return 'meta';
  }
  if (haystack.includes('organic') || haystack.includes('google') || haystack.includes('seo')) {
    return 'organic';
  }
  if (haystack.includes('direct')) return 'direct';
  if (!referrer.trim()) return 'direct';
  if (/google|bing|yahoo|duckduckgo/i.test(referrer)) return 'organic';
  if (/facebook|instagram|meta/i.test(referrer)) return 'meta';
  return 'direct';
}

export function inferPasswordPopupContext(url: string): PasswordPopupTriggerContext {
  const path = url.split(/[?#]/)[0] ?? '';
  if (path.startsWith('/checkout') || path === '/cart') return 'cart';
  if (path.startsWith('/mi-cuenta')) return 'profile';
  if (
    /\/producto\//.test(path) ||
    /\/luxury-pass\//.test(path) ||
    /\/vales\//.test(path) ||
    /\/experiencias\/[^/]+/.test(path)
  ) {
    return 'view_item';
  }
  return 'item_list';
}

export function inferAddToCartSurface(url: string): 'item_list' | 'view_item' | 'home' | 'modal' {
  const path = url.split(/[?#]/)[0] ?? '';
  if (path === '/inicio' || path === '/') return 'home';
  if (path.startsWith('/checkout')) return 'modal';
  if (inferPasswordPopupContext(path) === 'view_item') return 'view_item';
  return 'item_list';
}

export function classifyLoginError(error: unknown): LoginErrorType {
  if (isTimeoutLike(error)) return 'timeout';
  const status = statusOf(error);
  if (status === 401 || status === 404) return 'not_found';
  return 'technical';
}

export function classifyCheckoutError(
  error: unknown,
): 'payment_declined' | 'timeout' | 'validation' | 'technical' | 'gateway_error' {
  if (isTimeoutLike(error)) return 'timeout';
  const status = statusOf(error);
  if (status === 422 || status === 400) return 'validation';
  if (status === 402 || status === 409) return 'payment_declined';
  if (status >= 500) return 'gateway_error';
  return 'technical';
}

function segmentPrefix(value?: string | null): string {
  return (value ?? '').replace(/\D/g, '').slice(0, 2);
}

function routePath(url: string): string {
  const path = url.split(/[?#]/)[0] ?? '';
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}

function currentTitle(): string {
  return typeof document === 'undefined' ? '' : document.title;
}

function pageLocation(path: string): string {
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

function defaultCategory(lineType: 'product' | 'voucher'): string {
  return lineType === 'voucher' ? BENEFIT_PASS_CATEGORY : PRODUCT_CATEGORY;
}

function firstText(...values: readonly (string | null | undefined)[]): string {
  for (const value of values) {
    const text = value?.trim() ?? '';
    if (text) return text;
  }
  return '';
}

function firstPositive(...values: readonly (number | null | undefined)[]): number {
  for (const value of values) {
    const parsed = money(value);
    if (parsed > 0) return parsed;
  }
  return 0;
}

function currentHref(): string {
  return typeof window === 'undefined' ? '' : window.location.href;
}

function currentReferrer(): string {
  return typeof document === 'undefined' ? '' : document.referrer;
}

function safeUrl(href: string): URL | null {
  try {
    return new URL(href, 'https://luxuryclub.pe');
  } catch {
    return null;
  }
}

function statusOf(error: unknown): number {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    return money((error as { status?: unknown }).status);
  }
  return 0;
}

function isTimeoutLike(error: unknown): boolean {
  if (statusOf(error) === 0) {
    return typeof error === 'object' && error !== null && 'status' in error;
  }
  return error instanceof Error && error.name === 'TimeoutError';
}
